import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Search, Filter } from "lucide-react";
import { format } from "date-fns";

interface IssueReport {
  id: string;
  user_name: string | null;
  user_email: string;
  issue_type: string;
  page_url: string | null;
  subject: string;
  description: string;
  browser_info: string | null;
  status: string;
  priority: string;
  admin_notes: string | null;
  resolved_at: string | null;
  created_at: string;
}

const IssueReports = () => {
  const { toast } = useToast();
  const [reports, setReports] = useState<IssueReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<IssueReport | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const { data, error } = await supabase
        .from("issue_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error: any) {
      console.error("Error fetching reports:", error);
      toast({
        title: "Error",
        description: "Failed to load issue reports",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReport = async () => {
    if (!selectedReport) return;

    try {
      const updates: any = {
        admin_notes: adminNotes,
        status: selectedStatus,
      };

      if (selectedStatus === "resolved" && !selectedReport.resolved_at) {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("issue_reports")
        .update(updates)
        .eq("id", selectedReport.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Issue report updated successfully",
      });

      setIsDialogOpen(false);
      fetchReports();
    } catch (error: any) {
      console.error("Error updating report:", error);
      toast({
        title: "Error",
        description: "Failed to update report",
        variant: "destructive",
      });
    }
  };

  const openDialog = (report: IssueReport) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || "");
    setSelectedStatus(report.status);
    setIsDialogOpen(true);
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      report.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.user_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || report.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || report.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new": return "bg-blue-500";
      case "in_review": return "bg-yellow-500";
      case "resolved": return "bg-green-500";
      case "wont_fix": return "bg-gray-500";
      default: return "bg-gray-500";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-500";
      case "high": return "bg-orange-500";
      case "medium": return "bg-yellow-500";
      case "low": return "bg-green-500";
      default: return "bg-gray-500";
    }
  };

  const getIssueTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      bug: "Bug",
      ui_issue: "UI Issue",
      performance: "Performance",
      content_error: "Content Error",
      other: "Other",
    };
    return labels[type] || type;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Issue Reports</h1>
          <p className="text-muted-foreground mt-2">
            Manage and respond to user-reported issues
          </p>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by subject or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Reports List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredReports.length === 0 ? (
          <Card className="p-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No issue reports found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReports.map((report) => (
              <Card
                key={report.id}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => openDialog(report)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStatusColor(report.status)}>
                        {report.status.replace("_", " ")}
                      </Badge>
                      <Badge className={getPriorityColor(report.priority)}>
                        {report.priority}
                      </Badge>
                      <Badge variant="outline">{getIssueTypeLabel(report.issue_type)}</Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {report.subject}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      From: {report.user_name || "Anonymous"} ({report.user_email})
                    </p>
                    {report.page_url && (
                      <p className="text-xs text-muted-foreground mb-2">
                        Page: {report.page_url}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(report.created_at), "PPpp")}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Issue Report Details</DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-4">
                <div>
                  <Label>Subject</Label>
                  <p className="text-sm">{selectedReport.subject}</p>
                </div>
                <div>
                  <Label>Description</Label>
                  <p className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">
                    {selectedReport.description}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type</Label>
                    <p className="text-sm">{getIssueTypeLabel(selectedReport.issue_type)}</p>
                  </div>
                  <div>
                    <Label>From</Label>
                    <p className="text-sm">
                      {selectedReport.user_name || "Anonymous"} ({selectedReport.user_email})
                    </p>
                  </div>
                </div>
                {selectedReport.page_url && (
                  <div>
                    <Label>Page URL</Label>
                    <p className="text-sm break-all">{selectedReport.page_url}</p>
                  </div>
                )}
                {selectedReport.browser_info && (
                  <div>
                    <Label>Browser Info</Label>
                    <p className="text-xs text-muted-foreground">{selectedReport.browser_info}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_review">In Review</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="wont_fix">Won't Fix</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <p className="text-sm capitalize">{selectedReport.priority}</p>
                  </div>
                </div>
                <div>
                  <Label htmlFor="admin_notes">Admin Notes</Label>
                  <Textarea
                    id="admin_notes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this issue..."
                    rows={4}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateReport}>
                    Update Report
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default IssueReports;
